/**
 * P3 — tiny colored dot rendered next to a token row to signal the row's chain-
 * level status (scanning/stale/error/poisoned). Hidden when status is `ok` so
 * the happy path stays visually quiet. Pure presentational; data comes from
 * `useRailgunChainStatus(chainId)` at the call site.
 */
import React from 'react'
import { View, StyleSheet } from 'react-native'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import type { RailgunChainRowStatus } from './useRailgunChainStatus'

const COLOR_BY_STATUS: Record<RailgunChainRowStatus, string> = {
  ok: '#17c964',
  fetching: '#f5a524',
  scanning: '#f5a524',
  stale: '#9aa3b2',
  error: '#f31260',
  poisoned: '#f31260'
}

const TOOLTIP_BY_STATUS: Record<RailgunChainRowStatus, string> = {
  ok: 'Balance verified against latest merkle root',
  fetching: 'Refreshing balance…',
  scanning: 'Scanning merkle tree…',
  stale: 'Balance may be out of date — waiting for next refresh',
  error: 'Failed to refresh balance — will retry',
  poisoned: 'Local cache out of sync with on-chain merkle root'
}

export type RailgunChainRowIconProps = {
  status: RailgunChainRowStatus | null
  tooltipId?: string
}

const RailgunChainRowIcon: React.FC<RailgunChainRowIconProps> = ({ status, tooltipId }) => {
  if (!status || status === 'ok') return null
  const color = COLOR_BY_STATUS[status]
  return (
    <View
      style={[styles.dot, { backgroundColor: color }]}
      dataSet={
        tooltipId
          ? createGlobalTooltipDataSet({ id: tooltipId, content: TOOLTIP_BY_STATUS[status] })
          : undefined
      }
    />
  )
}

const styles = StyleSheet.create({
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 4
  }
})

export default RailgunChainRowIcon
